import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Flex,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  OutlineButton,
  PrimaryButton,
  StatusBadge,
  InlineLoader,
} from '../../../components/ui';
import { indentStatusBadge } from '../../../data/master/assetOwner';
import { assignPortalIndent } from '../../../repositories/publicVendor.repository';
import { formatDobDisplay } from '../../../utils/personName';
import { formatIndianDateTimeParts } from '../../../utils/datetime';
import { formatTimePeriod } from '../../../utils/timePeriod';

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <Box
      p={3}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      <Text fontSize="12px" color="text.secondary" mb={1} fontWeight="500">
        {label}
      </Text>
      <Text fontSize="15px" fontWeight="600" whiteSpace="pre-wrap" lineHeight="1.4">
        {value}
      </Text>
    </Box>
  );
}

function DateChips({ value }) {
  const parts = formatIndianDateTimeParts(value);
  if (!parts.date && !parts.time) return <Text color="text.secondary">—</Text>;
  return (
    <Flex gap={1.5} wrap="wrap">
      {parts.date ? <StatusBadge status="neutral">{parts.date}</StatusBadge> : null}
      {parts.time ? <StatusBadge status="info">{parts.time}</StatusBadge> : null}
    </Flex>
  );
}

function EquipmentPhoto({ urls, alt, show }) {
  const list = useMemo(() => {
    const raw = Array.isArray(urls) ? urls.filter(Boolean) : [];
    return raw.length ? raw : [];
  }, [urls]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [list.join('|')]);

  useEffect(() => {
    if (!show || list.length < 2) return undefined;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % list.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [show, list.length, list.join('|')]);

  if (!show) {
    return (
      <Box
        w={{ base: '100%', sm: '120px' }}
        h="88px"
        borderRadius="sm"
        bg="blackAlpha.50"
        flexShrink={0}
      />
    );
  }
  if (!list.length) {
    return (
      <Flex
        w={{ base: '100%', sm: '120px' }}
        h="88px"
        borderRadius="sm"
        bg="blackAlpha.100"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Text fontSize="12px" color="text.secondary">
          No photo
        </Text>
      </Flex>
    );
  }

  const src = list[index % list.length];
  return (
    <Box
      position="relative"
      w={{ base: '100%', sm: '120px' }}
      h="88px"
      borderRadius="sm"
      overflow="hidden"
      flexShrink={0}
      bg="blackAlpha.100"
    >
      <Image
        key={src}
        src={src}
        alt={alt || 'Equipment'}
        w="100%"
        h="100%"
        objectFit="cover"
        transition="opacity 0.35s ease"
      />
      {list.length > 1 ? (
        <Flex
          position="absolute"
          bottom={1}
          left={0}
          right={0}
          justify="center"
          gap={1}
        >
          {list.map((_, i) => (
            <Box
              key={i}
              w="6px"
              h="6px"
              borderRadius="full"
              bg={i === index % list.length ? 'white' : 'whiteAlpha.500'}
              boxShadow="0 0 2px rgba(0,0,0,0.4)"
            />
          ))}
        </Flex>
      ) : null}
    </Box>
  );
}

export function VendorPortalRequestModal({
  isOpen,
  onClose,
  indent,
  loading,
  portalToken,
  onAssigned,
}) {
  const toast = useToast();
  const confirmDlg = useDisclosure();
  const cancelRef = React.useRef();
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const line = indent?.line || {};
  const recs = Array.isArray(indent?.recommendations) ? indent.recommendations : [];
  const period =
    formatTimePeriod(line.requiredFrom, line.requiredTill) || line.timePeriod || '';
  const qtyLabel = [line.quantity, line.uom].filter(Boolean).join(' ');
  const capacityLabel = [line.capacity, line.capacityUom].filter(Boolean).join(' ');

  const alreadyAssigned = Boolean(
    indent?.assignment?.assetDbId ||
      indent?.assignment?.assetId ||
      String(indent?.status || '').toLowerCase() === 'assigned',
  );
  const assignedEquipmentId =
    indent?.assignment?.assetDbId ||
    indent?.assignment?.equipment?.id ||
    null;

  const canGiveWork =
    !alreadyAssigned &&
    (indent?.status === 'Recommended' || indent?.status === 'Pending') &&
    recs.length > 0;

  useEffect(() => {
    if (!isOpen) {
      setSelectedEquipmentId(null);
      return;
    }
    if (assignedEquipmentId) {
      setSelectedEquipmentId(Number(assignedEquipmentId));
    } else {
      setSelectedEquipmentId(null);
    }
  }, [isOpen, indent?.id, assignedEquipmentId]);

  const selectedRec = useMemo(
    () =>
      recs.find(
        (r) =>
          Number(r.assetDbId) === Number(selectedEquipmentId) ||
          Number(r.id) === Number(selectedEquipmentId),
      ) || null,
    [recs, selectedEquipmentId],
  );

  const openConfirm = () => {
    if (!selectedRec?.assetDbId) {
      toast({
        title: 'Select one equipment first',
        status: 'warning',
        position: 'top',
      });
      return;
    }
    confirmDlg.onOpen();
  };

  const confirmGiveWork = async () => {
    if (!portalToken || !indent?.id || !selectedRec?.assetDbId) return;
    setAssigning(true);
    try {
      const updated = await assignPortalIndent(portalToken, indent.id, {
        equipmentId: selectedRec.assetDbId,
      });
      confirmDlg.onClose();
      toast({
        title: 'Work given',
        description: `${selectedRec.assetId || 'Equipment'} is now assigned to this request.`,
        status: 'success',
        position: 'top',
      });
      if (typeof onAssigned === 'function') {
        await onAssigned(updated);
      }
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to give work',
        status: 'error',
        position: 'top',
      });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxH="90vh">
          <ModalHeader pr={12}>
            <Text fontSize="13px" color="text.secondary" fontFamily="mono" mb={1}>
              {indent?.indentId || 'Request'}
            </Text>
            {indent?.line?.equipmentType || indent?.line?.specification || 'Request details'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            {loading ? (
              <InlineLoader label="Loading request…" />
            ) : !indent ? (
              <Text color="text.secondary">Request not found.</Text>
            ) : (
              <VStack align="stretch" spacing={5}>
                <Flex align="center" justify="space-between" gap={3} wrap="wrap">
                  <Box>
                    <Text fontSize="12px" color="text.secondary" mb={1} fontWeight="500">
                      Added on
                    </Text>
                    <DateChips value={indent.createdAt} />
                  </Box>
                  <StatusBadge status={indentStatusBadge(indent.status)}>
                    {indent.status}
                  </StatusBadge>
                </Flex>

                <Box>
                  <Heading size="sm" mb={1}>
                    Recommended equipment
                  </Heading>
                  {canGiveWork ? (
                    <Text fontSize="13px" color="text.secondary" mb={2}>
                      Tap one vehicle to select. Others dim slightly. Photos rotate every few
                      seconds. Confirm before giving work.
                    </Text>
                  ) : null}
                  {recs.length === 0 ? (
                    <Text fontSize="14px" color="text.secondary">
                      No recommendations yet. Utkarsh staff will shortlist equipment for this
                      request.
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {recs.map((rec) => {
                        const eqId = Number(rec.assetDbId);
                        const isSelected =
                          selectedEquipmentId != null &&
                          Number(selectedEquipmentId) === eqId;
                        const dimOthers =
                          selectedEquipmentId != null && !isSelected;
                        const showPhoto = !dimOthers;
                        const photoUrls =
                          Array.isArray(rec.photoUrls) && rec.photoUrls.length
                            ? rec.photoUrls
                            : rec.photoUrl
                              ? [rec.photoUrl]
                              : [];

                        return (
                          <Box
                            key={rec.recId || rec.id}
                            as={canGiveWork ? 'button' : 'div'}
                            type={canGiveWork ? 'button' : undefined}
                            textAlign="left"
                            p={3}
                            bg="bg.input"
                            borderRadius="sm"
                            borderWidth="2px"
                            borderColor={isSelected ? 'utkarsh.amber' : 'border.subtle'}
                            cursor={canGiveWork ? 'pointer' : 'default'}
                            onClick={
                              canGiveWork
                                ? () =>
                                    setSelectedEquipmentId((prev) =>
                                      Number(prev) === eqId ? null : eqId,
                                    )
                                : undefined
                            }
                            filter={dimOthers ? 'blur(1.5px)' : 'none'}
                            opacity={dimOthers ? 0.72 : 1}
                            transition="filter 0.2s ease, opacity 0.2s ease, border-color 0.15s ease"
                            overflow="hidden"
                          >
                            <Flex gap={3} align="start" direction={{ base: 'column', sm: 'row' }}>
                              <EquipmentPhoto
                                urls={photoUrls}
                                alt={rec.equipmentName || rec.assetId}
                                show={showPhoto}
                              />
                              <Box flex="1" minW={0}>
                                <Flex justify="space-between" gap={2} wrap="wrap" mb={1}>
                                  <Text fontWeight="600">
                                    {rec.equipmentName ||
                                      rec.equipmentType ||
                                      rec.assetId ||
                                      'Equipment'}
                                  </Text>
                                  {!dimOthers ? (
                                    <StatusBadge
                                      status={
                                        isSelected || rec.status === 'Selected'
                                          ? 'verified'
                                          : indentStatusBadge(rec.status) || 'neutral'
                                      }
                                    >
                                      {isSelected || rec.status === 'Selected'
                                        ? alreadyAssigned
                                          ? 'Work given'
                                          : 'Selected'
                                        : rec.status || 'Recommended'}
                                    </StatusBadge>
                                  ) : (
                                    <StatusBadge status="neutral">Not selected</StatusBadge>
                                  )}
                                </Flex>
                                {!dimOthers ? (
                                  <>
                                    <Text fontSize="13px" color="text.secondary" fontFamily="mono">
                                      {rec.assetId}
                                      {rec.regNo ? ` · ${rec.regNo}` : ''}
                                    </Text>
                                    {[rec.manufacturer, rec.model].filter(Boolean).length ? (
                                      <Text fontSize="13px" color="text.secondary">
                                        {[rec.manufacturer, rec.model].filter(Boolean).join(' ')}
                                      </Text>
                                    ) : null}
                                    {rec.remark ? (
                                      <Text fontSize="13px" mt={1}>
                                        {rec.remark}
                                      </Text>
                                    ) : null}
                                  </>
                                ) : (
                                  <Text fontSize="13px" color="text.secondary" fontFamily="mono">
                                    {rec.assetId || '—'}
                                  </Text>
                                )}
                              </Box>
                            </Flex>
                          </Box>
                        );
                      })}
                    </VStack>
                  )}
                </Box>

                <Box>
                  <Heading size="sm" mb={3}>
                    Request details
                  </Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <DetailRow label="Work area" value={indent.workArea} />
                    <DetailRow label="Equipment type" value={line.equipmentType} />
                    <DetailRow label="Specification" value={line.specification} />
                    <DetailRow label="Quantity" value={qtyLabel} />
                    <DetailRow label="Capacity" value={capacityLabel} />
                    <DetailRow label="Working hours" value={line.workingHours} />
                    <DetailRow
                      label="Required from"
                      value={formatDobDisplay(line.requiredFrom)}
                    />
                    <DetailRow
                      label="Required till"
                      value={formatDobDisplay(line.requiredTill)}
                    />
                    <DetailRow label="Period" value={period} />
                    <DetailRow label="Remarks" value={line.remarks} />
                  </SimpleGrid>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <OutlineButton onClick={onClose}>Close</OutlineButton>
            {canGiveWork ? (
              <PrimaryButton
                onClick={openConfirm}
                isDisabled={!selectedRec?.assetDbId}
              >
                Give work
              </PrimaryButton>
            ) : null}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={confirmDlg.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={confirmDlg.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="700">
              Confirm give work
            </AlertDialogHeader>
            <AlertDialogBody>
              Give this work to{' '}
              <Text as="span" fontWeight="700">
                {selectedRec?.equipmentName || selectedRec?.assetId || 'selected equipment'}
              </Text>
              {selectedRec?.regNo ? ` (${selectedRec.regNo})` : ''}? This cannot be undone from
              the portal without releasing later.
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <OutlineButton ref={cancelRef} onClick={confirmDlg.onClose} isDisabled={assigning}>
                Cancel
              </OutlineButton>
              <PrimaryButton onClick={confirmGiveWork} isLoading={assigning}>
                Yes, give work
              </PrimaryButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
